'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
{
  name: 'Priya Mehta',
  role: 'Student, Intermediate Batch',
  city: 'Ahmedabad',
  quote: 'Ravi sir has transformed the way I experience Garba. The training is so structured and joyful — I went from two left feet to performing on stage in just 3 months!',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10120f91e-1763301853398.png",
  alt: 'Priya Mehta smiling student in traditional Garba chaniya choli',
  rating: 5,
  batch: 'Intermediate'
},
{
  name: 'Ketan Shah',
  role: 'Parent of two students',
  city: 'Surat',
  quote: 'Both my kids (ages 8 and 12) are in the Beginners batch. The instructors are incredibly patient and the environment is very safe. My children look forward to every class!',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa7d9ef6-1763296853575.png",
  alt: 'Ketan Shah proud parent with warm smile',
  rating: 5,
  batch: 'Beginners'
},
{
  name: 'Sneha Joshi',
  role: 'Advanced Batch — Competition Winner',
  city: 'Vadodara',
  quote: 'I won first place at the Gujarat State Garba Championship after training with Ankit sir for just one season. The competition-level coaching here is unmatched in the city.',
  image: "https://images.unsplash.com/photo-1698407070519-eb4b8ba49642",
  alt: 'Sneha Joshi holding competition trophy in traditional Garba attire',
  rating: 5,
  batch: 'Advanced'
}];


export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden, .reveal-hidden-left, .reveal-hidden-right').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="testimonials" className="py-28 bg-bg-warm relative overflow-hidden" ref={sectionRef}>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Left — Quote */}
          <div className="reveal-hidden-left">
            <p className="text-primary font-bold uppercase tracking-[0.3em] text-[11px] mb-4">Student Stories</p>
            <h2 className="font-display text-5xl md:text-6xl font-black text-gradient-warm italic leading-tight mb-10">
              Real Dancers.<br />Real Joy.
            </h2>

            <div className="space-y-6">
              <div className="flex gap-1 mb-2">
                {Array.from({ length: testimonials?.[active]?.rating })?.map((_, i) =>
                <span key={i} className="star text-xl">★</span>
                )}
              </div>
              <p className="font-display text-2xl md:text-3xl italic leading-relaxed text-foreground">
                &ldquo;{testimonials?.[active]?.quote}&rdquo;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary/20 shrink-0">
                  <AppImage
                    src={testimonials?.[active]?.image}
                    alt={testimonials?.[active]?.alt}
                    className="w-full h-full object-cover" />
                  
                </div>
                <div>
                  <p className="font-bold text-foreground">{testimonials?.[active]?.name}</p>
                  <p className="text-xs text-muted uppercase tracking-wide">{testimonials?.[active]?.role} · {testimonials?.[active]?.city}</p>
                </div>
                <span className="ml-auto text-[10px] font-black uppercase tracking-widest bg-primary/10 text-primary px-3 py-1.5 rounded-full">
                  {testimonials?.[active]?.batch}
                </span>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3 mt-8">
              {testimonials?.map((_, i) =>
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                i === active ? 'bg-primary w-8' : 'bg-primary/25 w-2'}`
                }
                aria-label={`Testimonial ${i + 1}`} />

              )}
            </div>
          </div>

          {/* Right — Image + floating cards */}
          <div className="relative reveal-hidden-right">
            <div className="rounded-4xl overflow-hidden shadow-warm-xl aspect-[4/5]">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1af1a7fe5-1768972940296.png"
                alt="Joyful group of Garba students performing together in vibrant costumes during Navratri"
                className="w-full h-full object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-dark/40 via-transparent to-transparent" />
            </div>

            {/* Floating stat cards */}
            <div className="absolute -bottom-6 -left-6 glass rounded-3xl p-5 shadow-warm-lg">
              <p className="font-display text-3xl font-bold text-primary">2,400+</p>
              <p className="text-xs text-muted font-semibold mt-1">Happy Students Trained</p>
            </div>
            <div className="absolute -top-4 -right-4 glass rounded-3xl p-4 shadow-warm-md">
              <p className="text-sm font-bold text-foreground">🏆 State Champions</p>
              <p className="text-xs text-muted mt-0.5">40+ competition wins</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}